import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Linkedin, Phone, ArrowUpRight, Send, Check } from "lucide-react";
import axios from "axios";
import { PROFILE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LET = ["L", "E", "T", "'", "S"];
const TLK = ["T", "A", "L", "K"];

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const change = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus("error");
      setErrorMsg("Please fill in name, email and message.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please email me directly.");
    }
  };

  if (status === "sent") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-black p-8 md:p-10 bg-[var(--accent-soft)]"
        data-testid="contact-form-success"
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-full bg-[var(--ink)] text-[var(--bg)] flex items-center justify-center">
            <Check size={18} />
          </span>
          <span className="overline">Brief received</span>
        </div>
        <h3 className="font-display text-3xl md:text-4xl mb-3">Thanks — talk soon.</h3>
        <p className="text-black/70">
          I'll come back to you within two working days. In the meantime, feel free to{" "}
          <a href={PROFILE.linkedin} target="_blank" rel="noopener noreferrer" className="link-underline font-semibold">
            connect on LinkedIn
          </a>
          .
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-xs uppercase tracking-[0.22em] font-semibold link-underline"
          data-testid="contact-form-reset"
        >
          Send another note ↺
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="border border-black/20 p-6 md:p-8 bg-[var(--bg)]" data-testid="contact-form">
      <div className="overline mb-5">— Send a brief</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <label className="block">
          <span className="overline block mb-2">Name *</span>
          <input
            type="text"
            value={form.name}
            onChange={change("name")}
            required
            className="w-full bg-transparent border-b border-black/40 focus:border-black outline-none py-2 text-lg font-medium"
            data-testid="contact-input-name"
          />
        </label>
        <label className="block">
          <span className="overline block mb-2">Email *</span>
          <input
            type="email"
            value={form.email}
            onChange={change("email")}
            required
            className="w-full bg-transparent border-b border-black/40 focus:border-black outline-none py-2 text-lg font-medium"
            data-testid="contact-input-email"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="overline block mb-2">Company</span>
          <input
            type="text"
            value={form.company}
            onChange={change("company")}
            className="w-full bg-transparent border-b border-black/40 focus:border-black outline-none py-2 text-lg font-medium"
            data-testid="contact-input-company"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="overline block mb-2">What shall we discuss? *</span>
          <textarea
            value={form.message}
            onChange={change("message")}
            rows={4}
            required
            className="w-full bg-transparent border-b border-black/40 focus:border-black outline-none py-2 text-lg leading-relaxed resize-none"
            data-testid="contact-input-message"
          />
        </label>
      </div>

      {status === "error" && (
        <div className="mb-4 text-sm text-red-700" data-testid="contact-form-error">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="cta-pill disabled:opacity-60"
        data-testid="contact-form-submit"
      >
        <span>{status === "loading" ? "Sending…" : "Send brief"}</span>
        <Send size={14} />
      </button>
    </form>
  );
};

const Contact = () => {
  return (
    <section
      id="contact"
      className="relative pt-24 md:pt-28 pb-10 px-6 md:px-10 lg:px-14 bg-[var(--bg)] scroll-mt-16"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="overline mb-8"
        >
          — Contact
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-start mb-16 md:mb-20">
          {/* Vertical "Let's Talk" stack + contact details */}
          <div className="col-span-12 lg:col-span-5">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <a href={`mailto:${PROFILE.email}`} className="block group" data-testid="contact-stacked-cta">
                {/* MOBILE: single horizontal line "LET'S TALK." */}
                <div className="lg:hidden font-display text-[18vw] sm:text-[14vw] leading-[0.95] tracking-tight group-hover:text-[var(--accent)] transition-colors">
                  LET'S TALK.
                </div>
                {/* DESKTOP: vertical letter stack */}
                <div className="hidden lg:flex font-display text-[8vw] leading-[0.85] flex-col tracking-tight">
                  {LET.map((c, idx) => (
                    <motion.span
                      key={`l-${idx}`}
                      initial={{ y: 40, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.04, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="vstack-letter group-hover:text-[var(--accent)] transition-colors"
                    >
                      {c}
                    </motion.span>
                  ))}
                  <span className="block h-3" />
                  {TLK.map((c, idx) => (
                    <motion.span
                      key={`t-${idx}`}
                      initial={{ y: 40, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: (LET.length + idx) * 0.04, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      className="vstack-letter group-hover:text-[var(--accent)] transition-colors"
                    >
                      {c}
                    </motion.span>
                  ))}
                </div>
              </a>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="space-y-3 mt-10 max-w-md"
            >
              <li>
                <a
                  href={`mailto:${PROFILE.email}`}
                  className="group flex items-center justify-between border-b border-black/30 py-3 hover:border-black"
                  data-testid="contact-email"
                >
                  <span className="flex items-center gap-3">
                    <Mail size={18} />
                    <span className="font-display text-lg md:text-xl">{PROFILE.email}</span>
                  </span>
                  <ArrowUpRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:rotate-12 transition-all" />
                </a>
              </li>
              <li>
                <a
                  href={PROFILE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between border-b border-black/30 py-3 hover:border-black"
                  data-testid="contact-linkedin"
                >
                  <span className="flex items-center gap-3">
                    <Linkedin size={18} />
                    <span className="font-display text-lg md:text-xl">{PROFILE.linkedinHandle}</span>
                  </span>
                  <ArrowUpRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:rotate-12 transition-all" />
                </a>
              </li>
              <li>
                <a
                  href={`tel:${PROFILE.phone.replace(/\s/g, "")}`}
                  className="group flex items-center justify-between border-b border-black/30 py-3 hover:border-black"
                  data-testid="contact-phone"
                >
                  <span className="flex items-center gap-3">
                    <Phone size={18} />
                    <span className="font-display text-lg md:text-xl">{PROFILE.phone}</span>
                  </span>
                  <ArrowUpRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:rotate-12 transition-all" />
                </a>
              </li>
            </motion.ul>
          </div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 lg:col-span-7"
          >
            <h3 className="font-display text-3xl md:text-5xl mb-6 leading-tight">
              Have a brief? Send it over.
            </h3>
            <ContactForm />
          </motion.div>
        </div>

        <footer className="border-t border-black/20 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="footer">
          <div className="overline">© {new Date().getFullYear()} Mia O'Neill</div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="overline link-underline"
            data-testid="back-to-top"
          >
            Back to top ↑
          </button>
        </footer>
      </div>
    </section>
  );
};

export default Contact;
